import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';

/*models*/

import { RootState } from '../../../store';
import {
    IAccount,
    ICancelReasonItem,
    IHold,
    IRelatedItems,
} from '../../../models/order';
import { IOrderDetails } from '../../../models/order';
import { ISession } from '../../../models/pro-sites';
import { ICompanyAccount } from '../../../models/company-account';
import {
    IShippingMethodOptions,
    IShippingMethods,
} from '../../../models/shipping';
import { IAccountBillTo } from '../../../models/account';

/*actions*/

import { loadShipToAccounts } from '../../../store/account/account-actions';
import {
    loadOrder,
    loadCancelReasons,
    postLineToGetRelatedItems,
    reorder,
    lineItemPTTrue,
    lineItemPTFalse,
    clearRelatedItems,
    clearReorder,
    updateSelect,
} from '../../../store/order/order-actions';
import { loadProductDetails } from '../../../store/product/product-detail-actions';
import { loadShippingMethods } from '../../../store/shipping/shipping-actions';
import { orderHold } from '../../../api/order-api';

/* components */

import ShippingDropdown from './shipping-dropdown';
import BillingDropdown from './billing-dropdown';

import CancelledPizzaTracker from './pizza-trackers/cancelled-pizza-tracker';
import { PizzaTracker } from './pizza-trackers/pizza-tracker';

import OrderNotificationDashboard from './order-notification-dashboard';
import LineItemDisplay from './line-item-display';

import CancelModal from './modals/cancel-modal';
import ReorderMessageModal from './modals/reorder-popup-message-modal';
import AddItemModal from './modals/add-item-modal';
import EscalateModal from './modals/escalate-modal';
import ReorderModal from './modals/reorder-modal';

import './order-details-styles.css';
import OrderNotification from '../../common/order-notification';
import ConfirmShippingMethodModal from './modals/confirm-shipping-method-modal';
import OrderActionDropdown from './order-action-dropdown';
import { hasFeature } from '../../authorization/features';
import * as FC from '../../../models/feature-constants';
import * as CON from '../../common/constants';
import { OrderTracker } from './order-hub/order-tracker';
import { IOptions } from '../../inputs/select-input';
import { isMiracleEar } from '../../../hooks/miracle-ear-hook';

interface IOrderDetailsProps {
    order: IOrderDetails | null;
    relatedItems: IRelatedItems | null;
    shipToAccounts: ICompanyAccount['shipToAccounts'];
    billToAccounts: IAccountBillTo[];
    cancelReasons: ICancelReasonItem[] | null;
    shippingMethods: IShippingMethods[] | null;
    session: ISession | null;
    lineItemPT: boolean;
    reorderResult: boolean | null | undefined;
    selected: number | null;
    lineItemPTTrue: () => { type: string };
    lineItemPTFalse: () => { type: string };
    updateSelect: (select: number) => { type: string };
    clearRelatedItems: () => Promise<void>;
    clearReorder: () => { type: string };
    loadShipToAccounts: () => Promise<void>;
    loadOrder: (orderNumber: string) => Promise<void>;
    loadBillToAccounts: () => Promise<void>;
    loadCancelReasons: () => Promise<void>;
    postLineToGetRelatedItems: (parts: string[]) => Promise<void>;
    loadShippingMethods: (billTo: string, shipTo: string) => Promise<void>;
    loadProductDetails: (
        productGuid: string,
        cartId: string
    ) => Promise<boolean>;
}

let orderNumber: any = '';

export const OrderDetails = ({
    order,
    shipToAccounts,
    billToAccounts,
    cancelReasons,
    shippingMethods,
    session,
    lineItemPT,
    reorderResult,
    clearRelatedItems,
    loadShipToAccounts,
    loadOrder,
    loadCancelReasons,
    loadShippingMethods,
    clearReorder,
    selected,
    updateSelect,
}: IOrderDetailsProps) => {
    orderNumber = useLocation().state.orderNumber;
    const intl = useIntl();
    const [cancelHold, setCancelHold] = useState<IHold | undefined>(undefined);
    const [picked, setPicked] = useState(false);
    const [stockOrder, setStockOrder] = useState(false);
    const [shipped, setShipped] = useState(false);
    const [isEscalated, setIsEscalated] = useState(false);
    const [shippingSelected, setShippingSelected] = useState(0);
    const [disabledDD, setDisabledDD] = useState(false);
    const [someNoDeletes, setSomeNoDeletes] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    /*shipping methods */
    const [updatedShippingMethod, setUpdatedShippingMethod] =
        useState<IShippingMethodOptions | null>(null);
    const [filteredOptions, setFilteredOptions] = useState<
        IShippingMethodOptions[] | null
    >(null);
    const [selectedSM, setSelectedSM] = useState('option_1');
    const [confirmShippingMethodModal, setConfirmShippingMethodModal] =
        useState(false);

    let filteredMethods: IShippingMethodOptions[] = [];
    const isBuyItemsAgain =
        session &&
        (isMiracleEar(session.siteNumber) ||
            (!isMiracleEar(session.siteNumber) && stockOrder));

    const shippedOptions: IOptions[] = [
        {
            value: 1,
            text: intl.formatMessage({
                id: CON.REPORT_LOSS_DAMAGE_ID,
                defaultMessage: CON.REPORT_LOSS_DAMAGE,
            }),
            //to do: create a feature service insert for this feature
            optionalParam: FC.ORDER_MANAGEMENT,
            disabled: false,
        },
        ...(isBuyItemsAgain
            ? [
                  {
                      value: 2,
                      text: intl.formatMessage({
                          id: FC.BUY_ITEMS_AGAIN_ID,
                          defaultMessage: FC.BUY_ITEMS_AGAIN,
                      }),
                      optionalParam: FC.BUY_ITEMS_AGAIN,
                      disabled: false,
                  },
              ]
            : []),
    ];

    /*load the order*/
    useEffect(() => {
        if (order === null || order.orderNumber !== orderNumber) {
            loadOrder(orderNumber);
            return;
        }
        /*if there is a cancel hold on the order set cancel hold to true and 
        display a notification to the user */
        if (order.holds) {
            setCancelHold(
                order.holds.find(
                    (hold) => hold.name === 'ORDER CANCELLED FROM ESTORE'
                )
            );
        }

        //TO DO: SOP CHECK if name is null then set Stock to true
        //Need to update STANDARD_ORDER_AID based on what is sent in the payload for
        // the orderDetails call underneath the 'order.ordertype'.This is the initial change
        if (
            (order.patientName === null &&
                order.orderType === CON.STANDARD_ORDER_AID) ||
            (order.orderType === CON.HEARING_AID_ORDER &&
                (order.patientName === CON.STOCK ||
                    order.patientName === CON.STOCK_COMMA)) ||
            order.orderType === CON.COMMON_SUPPLY_ORDER
        ) {
            setStockOrder(true);
        }

        /*check status of line items for eligible statuses for activity
        many times orders will have 'cancelled' line items that does not mean
        the order has been cancelled, just tweaked so cancelled is an option
        comment for push*/
        order.lineItems.forEach((lineItem) => {
            const itemStatusLower = lineItem.status.toLowerCase();
            if (
                itemStatusLower !== CON.Status.ENTERED &&
                itemStatusLower !== CON.Status.BOOKED &&
                itemStatusLower !== CON.Status.AWAITING_SHIPPING &&
                itemStatusLower !== CON.Status.AWAITING_FULFILLMENT &&
                itemStatusLower !== CON.Status.CANCELLED &&
                itemStatusLower !== CON.Status.AWAITING_RETURN &&
                lineItem.shippableFlag === 'Y' &&
                session?.country === CON.US
            ) {
                setPicked(true);
            }
        });

        /* only if an order has been shipped reset picked to false
        so that even though line items are past picked status user can use the 
        shipped dropdown option -- a check against shipped status exists on disabledDD */
        const orderStatusLower = order.orderStatus.toLowerCase();
        if (
            orderStatusLower === CON.Status.SHIPPED ||
            orderStatusLower === CON.Status.DELIVERED ||
            order.shipDate
        ) {
            setShipped(true);
            setPicked(false);
        }
        if (order.orderEscalateDate) {
            setIsEscalated(true);
        }
        if (
            cancelHold ||
            orderStatusLower === CON.Status.CANCELLED ||
            picked ||
            (order.oracleOrderStatus &&
                order.oracleOrderStatus.toLowerCase() === 'entered')
        ) {
            setDisabledDD(true);
        }
        if (
            order!.lineItems.some(
                (lineItem) =>
                    lineItem.cancelEligible === 'N' &&
                    lineItem.shippableFlag === 'Y' &&
                    selected === 5
            )
        ) {
            /* if some are not deleteable send a message to the user when selected === 5 */
            setSomeNoDeletes(true);
        }
        if (orderStatusLower === CON.Status.CANCELLED) {
            setCancelled(true);
        }
    }, [order, picked, selected, someNoDeletes]);
    useEffect(() => {
        if (!cancelReasons) {
            loadCancelReasons();
        }
        if (!shipToAccounts || shipToAccounts.length === 0) {
            loadShipToAccounts();
        }
    }, []);
    useEffect(() => {
        /*load shipping methods */
        if (
            order &&
            hasFeature(session!, FC.CHANGE_SHIPPING_METHOD) &&
            !shippingMethods
        ) {
            loadShippingMethods(
                order.billTo.accountNumber,
                order.shipTo.accountNumber
            );
        }
    }, [order]);
    useEffect(() => {
        order &&
        (order.orderType.toLowerCase().includes('earmold') ||
            order.orderType.toLowerCase().includes('common') ||
            order.orderType.toLowerCase().includes('battery'))
            ? (filteredMethods = CON.shippingMethodOptions)
            : (filteredMethods = CON.shippingMethodOptions.filter(
                  (shipping) =>
                      !shipping.shippingMethod
                          .toLowerCase()
                          .includes('first') &&
                      !shipping.shippingMethod.toLowerCase().includes('ground')
              ));
        setFilteredOptions(filteredMethods);
    }, [order]);
    useEffect(() => {
        return () => {
            updateSelect(0);
            clearRelatedItems();
        };
    }, []);
    useEffect(() => {
        if (!selected) {
            updateSelect(0);
        }
    }, [selected]);

    const getOrderDisplayLine1 = (order: IOrderDetails) => {
        let value = '';
        if (
            order.orderType &&
            order.patientName &&
            order.agreement &&
            order.agreement.agreementName
        ) {
            value +=
                order.orderType +
                ' ' +
                intl.formatMessage({ id: 'order_for' }) +
                ' ' +
                order.patientName +
                ', ' +
                order.agreement.agreementName;

            if (order.agreement.patientNumber) {
                value +=
                    ' ' +
                    intl
                        .formatMessage({ id: 'Patient_#' })
                        .toLocaleLowerCase() +
                    ' ' +
                    order.agreement.patientNumber;
            }
        }
        return value;
    };

    const getOrderDisplayLine2 = (order: IOrderDetails) => {
        let value = '';
        if (order.placedByUser) {
            value +=
                intl.formatMessage({ id: CON.PLACED_BY }) +
                ' ' +
                order.placedByUser;
        }
        if (order.orderDate) {
            if (value.length > 0) {
                value +=
                    ' ' +
                    intl.formatMessage({ id: 'on' }) +
                    ' ' +
                    moment(order.orderDate).format('MMM DD, YYYY');
            } else {
                value +=
                    intl.formatMessage({ id: CON.PLACED_ON }) +
                    ' ' +
                    moment(order.orderDate).format('MMM DD, YYYY');
            }
        }
        return value;
    };

    const getAccountDisplayLine1 = (account: IAccount) => {
        let value = '';
        if (account.partyName) {
            value = account.partyName;
        }
        if (account.accountNumber) {
            if (value.length > 0) {
                value += ' ';
            }
            value += '(' + account.accountNumber + ')';
        }
        return value;
    };

    const getShippingDisplayLine2 = (order: IOrderDetails) => {
        let value = '';
        const totalAddress = [];
        let address: any;
        if (order.dropShip) {
            address = order.dropShip.address;
        } else {
            address = order.shipTo.address;
        }
        if (address) {
            if (address.street1 !== null && address.street1.length > 0) {
                totalAddress.push(address.street1);
            }
            if (address.street2 !== null && address.street2.length > 0) {
                totalAddress.push(address.street2);
            }
            if (address.street3 !== null && address.street3.length > 0) {
                totalAddress.push(address.street3);
            }
            if (address.street4 !== null && address.street4.length > 0) {
                totalAddress.push(address.street4);
            }
            if (address.city !== null && address.city.length > 0) {
                totalAddress.push(address.city);
            }
            if (address.state !== null && address.state.length > 0) {
                totalAddress.push(address.state);
            }
        }
        value = totalAddress.join(',');
        if (address.postalCode !== null && address.postalCode.length > 0) {
            value += ' ' + address.postalCode;
        }
        console.debug('getShippingDisplayLine2::final address is ' + value);
        return value;
    };
    const getBillingDisplayLine2 = (account: IAccount) => {
        let value = '';
        const totalAddress = [];
        const address = account.address;
        if (address) {
            if (address.street1 !== null && address.street1.length > 0) {
                totalAddress.push(address.street1);
            }
            if (address.street2 !== null && address.street2.length > 0) {
                totalAddress.push(address.street2);
            }
            if (address.street3 !== null && address.street3.length > 0) {
                totalAddress.push(address.street3);
            }
            if (address.street4 !== null && address.street4.length > 0) {
                totalAddress.push(address.street4);
            }
            if (address.city !== null && address.city.length > 0) {
                totalAddress.push(address.city);
            }
            if (address.state !== null && address.state.length > 0) {
                totalAddress.push(address.state);
            }
        }
        value = totalAddress.join(',');
        if (address.postalCode !== null && address.postalCode.length > 0) {
            value += ' ' + address.postalCode;
        }
        console.debug('getBillingDisplayLine2::final address is ' + value);
        return value;
    };
    const onChangeSM = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSM(e.target.value);
        const shipping = filteredOptions!.find(
            (shipping) => shipping.defaultShippingMethodCode === e.target.value
        ) as IShippingMethodOptions;
        setUpdatedShippingMethod({
            ...shipping,
        });

        setConfirmShippingMethodModal(true);
    };

    return (
        <>
            {order && order.orderNumber === orderNumber && (
                <div id="wrapper">
                    <div className="row" style={{ paddingTop: '0px' }}>
                        <div className="col-md-10">
                            <h1>
                                <FormattedMessage id={CON.ORDER_NUM} />
                                {' ' + order.orderNumber}
                            </h1>
                        </div>
                        {/*
                        <div className="col-md-2">
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    reorder(order.lineItems, order.orderNumber);
                                }}
                            >
                                <FormattedMessage id="Reorder" />
                            </button>
                        </div>
                        */}
                    </div>

                    <div className="row" style={{ paddingTop: '0px' }}>
                        <div className="col-md-12">
                            {getOrderDisplayLine1(order)}
                        </div>
                    </div>

                    <div className="row" style={{ paddingTop: '0px' }}>
                        <div className="col-md-12">
                            {getOrderDisplayLine2(order)}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <Link
                                style={{ borderBottom: 'solid 1px' }}
                                to={{
                                    pathname: '/my-account/order-history',
                                }}
                            >
                                &#60;{' '}
                                <FormattedMessage
                                    id={CON.VIEW_ALL_ORDERS_ID}
                                    defaultMessage={CON.VIEW_ALL_ORDERS}
                                />
                            </Link>
                        </div>
                        <div className="col-md-6">
                            {!order.trackingInfo
                                ? hasFeature(
                                      session!,
                                      FC.EXPECTED_SHIP_DATE
                                  ) && (
                                      <div className="shipmentTrackHead">
                                          <h4>
                                              <span className="fa fa-truck"></span>
                                              <FormattedMessage
                                                  id={CON.EXPECTED_SHIP_DATE_ID}
                                                  defaultMessage={
                                                      CON.EXPECTED_SHIP_DATE
                                                  }
                                              />
                                              {': '}
                                              <>
                                                  <span
                                                      style={{
                                                          fontSize: 'medium',
                                                      }}
                                                  >
                                                      {order.expectedShipDate ? (
                                                          order.shipDate ? (
                                                              moment
                                                                  .utc(
                                                                      order.shipDate
                                                                  )
                                                                  .format(
                                                                      'MMM DD, YYYY'
                                                                  )
                                                          ) : (
                                                              moment
                                                                  .utc(
                                                                      order.expectedShipDate
                                                                  )
                                                                  .format(
                                                                      'MMM DD, YYYY'
                                                                  )
                                                          )
                                                      ) : (
                                                          <FormattedMessage
                                                              id={
                                                                  CON.UNAVAILABLE_ID
                                                              }
                                                              defaultMessage={
                                                                  CON.UNAVAILABLE
                                                              }
                                                          />
                                                      )}
                                                  </span>
                                              </>
                                          </h4>
                                      </div>
                                  )
                                : order.trackingInfo.trackingUrl && (
                                      <div className="shipmentTrackHead">
                                          <h4>
                                              <span className="fa fa-truck"></span>
                                              {!order.orderEvent.hubOrderDetails
                                                  .length ? (
                                                  <FormattedMessage
                                                      id={CON.SHIPPED_DATE_ID}
                                                      defaultMessage={
                                                          CON.SHIPPED_DATE
                                                      }
                                                  />
                                              ) : (
                                                  <FormattedMessage
                                                      id="Expected_Ship_Date"
                                                      defaultMessage="Expected Ship Date"
                                                  />
                                              )}
                                              {': '}
                                              {order.shipDate &&
                                                  moment(order.shipDate).format(
                                                      'MMM DD, YYYY'
                                                  )}
                                          </h4>
                                          <p>
                                              <FormattedMessage
                                                  id={CON.TRACK_SHIPMENT_ID}
                                                  defaultMessage={
                                                      CON.TRACK_SHIPMENT
                                                  }
                                              />
                                              {': '}
                                              <a
                                                  href={
                                                      order.trackingInfo
                                                          .trackingUrl
                                                  }
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                              >
                                                  {
                                                      order.trackingInfo
                                                          .trackingNumber
                                                  }{' '}
                                                  <span className="fas fa-external-link-alt"></span>
                                              </a>
                                          </p>
                                      </div>
                                  )}
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <OrderNotificationDashboard order={order} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            {hasFeature(session!, FC.ORDER_TRACKER) ? (
                                <OrderTracker
                                    order={order}
                                    session={session!}
                                    cancelled={cancelled}
                                />
                            ) : (
                                <>
                                    {order.orderStatus === 'Cancelled' && (
                                        <CancelledPizzaTracker order={order} />
                                    )}
                                    {order.orderStatus !== 'Cancelled' && (
                                        <PizzaTracker order={order} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div
                        className="row"
                        style={{
                            paddingTop: '10px',
                            paddingBottom: '0px',
                        }}
                    >
                        <div className="col-md-6">
                            {selected === 2 ? (
                                <>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <label className="control-label">
                                                <strong>
                                                    <FormattedMessage id="Ship_To" />
                                                </strong>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <ShippingDropdown
                                                shipToAccounts={shipToAccounts}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="row orderAddresses">
                                        <div className="col-md-3">
                                            <label className="control-label">
                                                <strong>
                                                    <FormattedMessage id="Ship_To" />
                                                </strong>
                                            </label>
                                        </div>
                                        <div
                                            className="col-md-9"
                                            id="orderDetailsShipTo"
                                        >
                                            {getAccountDisplayLine1(
                                                order.shipTo
                                            )}
                                            <br />
                                            {getShippingDisplayLine2(order)}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="row" style={{ paddingTop: '20px' }}>
                                <div className="col-md-3">
                                    <label className="control-label">
                                        <strong>
                                            <FormattedMessage
                                                id={CON.SHIPPING_METHOD_ID}
                                                defaultMessage={
                                                    CON.SHIPPING_METHOD
                                                }
                                            />
                                        </strong>
                                    </label>
                                </div>
                                <div className="col-md-9">
                                    {selected === 1 &&
                                    shippingMethods &&
                                    filteredOptions ? (
                                        <select
                                            className="form-control search-all-option"
                                            onChange={onChangeSM}
                                            value={selectedSM}
                                        >
                                            <option
                                                value="option_1"
                                                disabled
                                                hidden
                                            >
                                                {intl.formatMessage({
                                                    id: CON.SELECT_ITEM_ID,
                                                    defaultMessage:
                                                        CON.SELECT_ITEM,
                                                })}
                                            </option>
                                            {filteredOptions.map(
                                                (option, i) => (
                                                    <option
                                                        key={i}
                                                        value={
                                                            option.defaultShippingMethodCode
                                                        }
                                                    >
                                                        {option.shippingMethod}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    ) : (
                                        <div className="row">
                                            <div
                                                className="col-md-6"
                                                id="orderDetailsShippingMethod"
                                            >
                                                {order.shippingMethod}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row" style={{ paddingTop: '20px' }}>
                                <div className="col-md-3">
                                    <label className="control-label">
                                        <strong>
                                            <FormattedMessage
                                                id={CON.SHIP_DATE_ID}
                                                defaultMessage={CON.SHIP_DATE}
                                            />
                                        </strong>
                                    </label>
                                </div>
                                <div
                                    className="col-md-9"
                                    id="orderDetailsShipDateTracking"
                                >
                                    {order.shipDate ? (
                                        <>
                                            {!order.orderEvent.hubOrderDetails
                                                .length
                                                ? intl.formatMessage({
                                                      id: CON.Status.SHIPPED,
                                                  })
                                                : intl.formatMessage({
                                                      id: 'Expected_Ship_Date',
                                                      defaultMessage:
                                                          'Expected Ship Date',
                                                  }) + ' '}
                                            {moment(order.shipDate).format(
                                                'MMMDD, YYYY'
                                            )}
                                            <br />
                                            {order.trackingInfo &&
                                                order.trackingInfo
                                                    .trackingUrl && (
                                                    <a
                                                        href={
                                                            order.trackingInfo
                                                                .trackingUrl
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <FormattedMessage
                                                            id={
                                                                CON.TRACK_SHIPMENT
                                                            }
                                                            defaultMessage={
                                                                CON.TRACK_SHIPMENT
                                                            }
                                                        />{' '}
                                                        <span className="fas fa-external-link-alt"></span>
                                                    </a>
                                                )}
                                        </>
                                    ) : (
                                        <>
                                            {order.expectedShipDate &&
                                            hasFeature(
                                                session!,
                                                CON.EXPECTED_SHIP_DATE
                                            ) ? (
                                                <>
                                                    <FormattedMessage
                                                        id="Expected_to_ship_by:"
                                                        defaultMessage="Expected to ship by:"
                                                    />
                                                    <br />

                                                    {moment
                                                        .utc(
                                                            order.expectedShipDate
                                                        )
                                                        .format('MMM DD, YYYY')}
                                                </>
                                            ) : (
                                                <FormattedMessage
                                                    id="Pending"
                                                    defaultMessage="Pending"
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="row orderAddresses">
                                <div className="col-md-3">
                                    <label className="control-label">
                                        <strong>
                                            <FormattedMessage id="Bill_To" />
                                        </strong>
                                    </label>
                                </div>
                                {selected === 3 ? (
                                    <div className="col-md-9">
                                        <BillingDropdown
                                            billToAccounts={billToAccounts}
                                            order={order}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className="col-md-9"
                                        id="orderDetailsBillTo"
                                    >
                                        {order.billTo &&
                                            getAccountDisplayLine1(
                                                order.billTo
                                            )}
                                        <br />
                                        {order.billTo &&
                                            getBillingDisplayLine2(
                                                order.billTo
                                            )}
                                    </div>
                                )}
                            </div>
                            {session &&
                                !session.isAccountThirdParty &&
                                hasFeature(session!, FC.ORDER_MANAGEMENT) && (
                                    <>
                                        {disabledDD && !shipped && (
                                            <>
                                                <div
                                                    className="row"
                                                    style={{
                                                        paddingTop: '20px',
                                                    }}
                                                >
                                                    <div className="col-md-12">
                                                        <label className="control-label">
                                                            <strong>
                                                                <FormattedMessage
                                                                    id={
                                                                        CON.NEED_CHANGES_IN_ORDER_ID
                                                                    }
                                                                    defaultMessage={
                                                                        CON.NEED_TO_MAKE_CHANGES_TO_ORDER
                                                                    }
                                                                />
                                                            </strong>
                                                        </label>
                                                        <div className="tooltipCancel">
                                                            <span
                                                                className="fas fa-info-circle"
                                                                aria-hidden="true"
                                                            ></span>
                                                            <span className="tooltiptext">
                                                                <FormattedMessage
                                                                    id={
                                                                        CON.ORDER_DETAILS_TOOLTIP_ID
                                                                    }
                                                                    defaultMessage={
                                                                        CON.ORDER_DETAILS_TOOLTIP
                                                                    }
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    <div
                                                        className="col-md-12"
                                                        id="orderDetailsManageOrderDropdown"
                                                    >
                                                        <select
                                                            value={selected!}
                                                            className="form-control order-changes-dd"
                                                            disabled
                                                        >
                                                            <option
                                                                value={0}
                                                                disabled
                                                                hidden
                                                            >
                                                                {intl.formatMessage(
                                                                    {
                                                                        id: CON.SELECT_ITEM_ID,
                                                                        defaultMessage:
                                                                            CON.SELECT_ITEM,
                                                                    }
                                                                )}
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {!shipped && !disabledDD && (
                                            <>
                                                <OrderActionDropdown
                                                    order={order}
                                                    picked={picked}
                                                    stockOrder={stockOrder}
                                                />
                                            </>
                                        )}
                                        {shipped &&
                                            !isEscalated &&
                                            !disabledDD && (
                                                <>
                                                    <div
                                                        className="row"
                                                        style={{
                                                            paddingTop: '20px',
                                                        }}
                                                    >
                                                        <div className="col-md-12">
                                                            <label className="control-label">
                                                                <strong>
                                                                    <FormattedMessage
                                                                        id={
                                                                            CON.MANAGE_ORDER_ID
                                                                        }
                                                                        defaultMessage={
                                                                            CON.MANAGE_ORDER
                                                                        }
                                                                    />
                                                                </strong>
                                                            </label>
                                                            <div className="tooltipCancel">
                                                                <span
                                                                    className="fas fa-info-circle"
                                                                    aria-hidden="true"
                                                                ></span>
                                                                <span className="tooltiptext">
                                                                    <FormattedMessage
                                                                        id={
                                                                            CON.ORDER_DETAILS_TOOLTIP_ID
                                                                        }
                                                                        defaultMessage={
                                                                            CON.ORDER_DETAILS_TOOLTIP
                                                                        }
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12">
                                                            <select
                                                                value={
                                                                    shippingSelected
                                                                }
                                                                className="form-control"
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const choice: number =
                                                                        parseInt(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    setShippingSelected(
                                                                        choice
                                                                    );
                                                                }}
                                                            >
                                                                <option
                                                                    value={0}
                                                                >
                                                                    {intl.formatMessage(
                                                                        {
                                                                            id: CON.SELECT_ITEM_ID,
                                                                            defaultMessage:
                                                                                CON.SELECT_ITEM,
                                                                        }
                                                                    )}
                                                                </option>
                                                                {shippedOptions.map(
                                                                    (option) =>
                                                                        hasFeature(
                                                                            session!,
                                                                            option.optionalParam!
                                                                        ) && (
                                                                            <option
                                                                                key={
                                                                                    option.value
                                                                                }
                                                                                value={
                                                                                    option.value
                                                                                }
                                                                            >
                                                                                {
                                                                                    option.text
                                                                                }
                                                                            </option>
                                                                        )
                                                                )}
                                                            </select>
                                                        </div>
                                                        {shippingSelected ===
                                                            1 && (
                                                            <EscalateModal
                                                                order={order}
                                                                accountDisplay={
                                                                    getAccountDisplayLine1
                                                                }
                                                                billDisplay={
                                                                    getBillingDisplayLine2
                                                                }
                                                                shippingDisplay={
                                                                    getShippingDisplayLine2
                                                                }
                                                                onAccept={() =>
                                                                    undefined
                                                                }
                                                                onClose={() => {
                                                                    setShippingSelected(
                                                                        0
                                                                    );
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                    </>
                                )}
                        </div>
                    </div>

                    <hr />
                    {selected === 5 && someNoDeletes && (
                        <div style={{ paddingBottom: '20px' }}>
                            <OrderNotification className="alert-order alert-action">
                                <>
                                    <span className="fa fa-exclamation-triangle cancel-icon"></span>
                                    <FormattedMessage
                                        id={CON.CANNOT_UPATE_ID}
                                        defaultMessage={CON.CANNOT_UPDATE}
                                    />{' '}
                                    <button
                                        className="delete-items"
                                        onClick={() => {
                                            updateSelect(7);
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage={CON.CANCEL}
                                        />
                                    </button>{' '}
                                    <FormattedMessage
                                        id={CON.REORDER_ID}
                                        defaultMessage={CON.REORDER}
                                    />
                                </>
                            </OrderNotification>
                        </div>
                    )}
                    {/*line item display area */}
                    {order.lineItems && order.lineItems.length > 0 && (
                        <LineItemDisplay
                            session={session}
                            picked={picked}
                            shippingSelected={shippingSelected}
                        />
                    )}
                    {selected === 4 && (
                        <AddItemModal
                            onAccept={() => {
                                orderHold(order.orderNumber, false);
                                updateSelect(0);
                                loadOrder(order.orderNumber);
                            }}
                            order={order}
                            onClose={() => {
                                orderHold(order.orderNumber, false);
                                updateSelect(0);
                            }}
                        />
                    )}
                    {selected === 7 && (
                        <CancelModal
                            onAccept={() => undefined}
                            onClose={() => {
                                updateSelect(0);
                            }}
                            cancelReasonItems={cancelReasons!}
                            order={order}
                        />
                    )}
                    {(selected === 6 || shippingSelected === 2) && (
                        <ReorderModal
                            order={order}
                            onAccept={() => undefined}
                            onClose={() => {
                                updateSelect(0);
                                setShippingSelected(0);
                            }}
                        />
                    )}
                    {reorderResult && (
                        <ReorderMessageModal
                            onAccept={() => undefined}
                            cartItems={session!.carts.primary}
                            onClose={() => {
                                clearReorder();
                            }}
                        />
                    )}
                    {confirmShippingMethodModal && (
                        <ConfirmShippingMethodModal
                            order={order}
                            updatedShippingMethod={updatedShippingMethod!}
                            onAccept={() => {
                                setSelectedSM('option_1');
                                setConfirmShippingMethodModal(false);
                                updateSelect(0);
                                loadOrder(order.orderNumber);
                            }}
                            onClose={() => {
                                setSelectedSM('option_1');
                                setConfirmShippingMethodModal(false);
                            }}
                        />
                    )}
                </div>
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        shipToAccounts: state.shipToAccounts ? state.shipToAccounts.data! : [],
        orderNumber: orderNumber,
        order: state.order ? state.order.data! : null,
        billToAccounts: state.billToAccounts ? state.billToAccounts.data! : [],
        cancelReasons: state.cancelReasons ? state.cancelReasons.data! : null,
        shippingMethods: state.shippingMethods
            ? state.shippingMethods.data!
            : null,
        relatedItems: state.relatedItems ? state.relatedItems.data! : null,
        session: state.session ? state.session.data! : null,
        lineItemPT: state.lineItemPizzaTracker
            ? state.lineItemPizzaTracker
            : false,
        reorderResult: state.reorderResult ? state.reorderResult.data : null,
        selected: state.select,
    };
};

const mapDispatchToProps = {
    loadShipToAccounts,
    loadOrder,
    loadCancelReasons,
    loadShippingMethods,
    postLineToGetRelatedItems,
    loadProductDetails,
    reorder,
    lineItemPTTrue,
    lineItemPTFalse,
    clearRelatedItems,
    clearReorder,
    updateSelect,
};

const orderDetailsContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(OrderDetails);

export default orderDetailsContainer;
